import { ColumnStoreConfig, Model, ResourceModel } from "@bryntum/scheduler";
import { useCallback, useMemo } from "react";
import { OrderLineItemDto } from "../../../../dtos";
import { Link, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getSchedulingStatus } from "../../../../util/getSchedulingStatus";
import { formatTime } from "../../../../util/formatDate";
import { Warning, CheckCircle, Cancel } from "@mui/icons-material";
import { BryntumGridProps } from "@bryntum/scheduler-react";

// Column comparators must return 0 for equal values, otherwise Bryntum never falls through to the
// next sorter and multi sort (ctrl/cmd + click a header) silently ignores every column but the first.
const numericCompare = (a: unknown, b: unknown) =>
    String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });

// Sort hauler codes numerically so 100 follows 99 rather than 10. Codes are free-text,
// so alphanumeric codes still fall back to a sensible order instead of failing to parse.
// numericCompare returns 0 for equal codes, so multi sort still falls through to later columns.
const codeSortable = (lhs: any, rhs: any) => numericCompare(lhs?.data?.code, rhs?.data?.code);

const dateCompare = (a: unknown, b: unknown) => {
    const lhs = a ? new Date(a as string).getTime() : NaN;
    const rhs = b ? new Date(b as string).getTime() : NaN;
    const lhsInvalid = Number.isNaN(lhs);
    const rhsInvalid = Number.isNaN(rhs);
    if (lhsInvalid && rhsInvalid) return 0;
    if (lhsInvalid) return 1;
    if (rhsInvalid) return -1;
    return lhs - rhs;
};

const booleanCompare = (a: unknown, b: unknown) => {
    const lhs = Boolean(a);
    const rhs = Boolean(b);
    return lhs === rhs ? 0 : lhs ? 1 : -1;
};

// Do the sorters that come after `field` tell these two records apart? Bryntum stops at the first
// comparator that returns a non-zero value, so a column with a built-in tie-breaker has to ask this
// before applying it, otherwise the tie-breaker would override the user's secondary/tertiary sort.
const laterSortersSeparate = (field: string, lhs: any, rhs: any) => {
    const store = lhs?.firstStore;
    const sorters = store?.sorters ?? [];
    const index = sorters.findIndex((s: any) => s.field === field);
    const laterSorters = index === -1 ? [] : sorters.slice(index + 1);
    if (!laterSorters.length || !store?.createSorterFn) {
        return false;
    }
    return store.createSorterFn(laterSorters)(lhs, rhs) !== 0;
};

// Sort numerically by order number. Line items of the same order keep ascending line item order,
// unless the user has multi-sorted by further columns that can tell those line items apart.
const orderNumberSortable = (lhs: any, rhs: any) => {
    const orderDiff = numericCompare(lhs?.data?.orderNumber, rhs?.data?.orderNumber);
    if (orderDiff !== 0) return orderDiff;
    // Returning 0 hands the decision to the next sorter, which reaches the same conclusion.
    if (laterSortersSeparate('orderNumber', lhs, rhs)) return 0;
    const tiebreaker = numericCompare(lhs?.data?.orderLineItemNumber, rhs?.data?.orderLineItemNumber);
    const ascending = lhs?.firstStore?.sorters?.find((s: any) => s.field === 'orderNumber')?.ascending !== false;
    return ascending ? tiebreaker : -tiebreaker;
};

export function useColumnManager() {
    const navigate = useNavigate();

    const getOrderLineItemFromResourceModel = useCallback((data: { record: Model | ResourceModel }) => {
        return (data.record as any).data as OrderLineItemDto;
    }, []);

    const orderViewSchedulerDefaultColumnConfigs: ColumnStoreConfig = useMemo(() => ({
        data: [
            {
                id: 'customerName',
                field: 'customerName',
                width: 150,
                text: 'Customer',
                locked: true,
                cellCls: 'dispatch-customer-name-cell',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).customerName}`,
            },
            {
                id: 'customerNumber',
                field: 'customerNumber',
                width: 120,
                text: 'Customer #',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).customerNumber ?? ''}`,
            },
            {
                id: 'onsiteTime',
                field: 'onsiteTime',
                width: 100,
                text: 'Onsite Time',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
                renderer: (data: { record: ResourceModel }) => {
                    const onsiteTime = getOrderLineItemFromResourceModel(data).onsiteTime;
                    const formattedOnsiteTime = onsiteTime ? formatTime(onsiteTime) : 'ASAP';
                    return `${formattedOnsiteTime}`;
                },
                groupRenderer: ({ groupRowFor }) => {
                    return groupRowFor !== '!!novalue!!' ? formatTime(groupRowFor) : 'ASAP';
                },
            },
            {
                id: 'orderNumber',
                field: 'orderNumber',
                width: 100,
                text: 'Order #',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
                renderer: (data: { record: ResourceModel }) => (
                    <Typography>
                        <Link
                            href={`/job/${getOrderLineItemFromResourceModel(data).jobId}/order/${getOrderLineItemFromResourceModel(data).orderId}`}
                            variant='body2'
                            onClick={() => {
                                navigate(`/job/${getOrderLineItemFromResourceModel(data).jobId}/order/${getOrderLineItemFromResourceModel(data).orderId}`);
                            }}>
                            #{getOrderLineItemFromResourceModel(data).orderNumber}
                        </Link>
                    </Typography>
                ),
                sortable: orderNumberSortable,
            },
            {
                id: 'poNumber',
                field: 'poNumber',
                width: 120,
                text: 'PO #',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).poNumber ?? ''}`,
            },
            {
                id: 'projectName',
                field: 'projectName',
                width: 150,
                text: 'Project',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'jobName',
                field: 'jobName',
                width: 150,
                text: 'Job',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'description',
                field: 'name',
                width: 100,
                text: 'Description',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'equipmentType',
                field: 'equipmentTypeType',
                width: 50,
                text: 'Equipment Type',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'requirements',
                field: 'requirements',
                width: 100,
                text: 'Requirements',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'siteName',
                field: 'siteName',
                width: 50,
                text: 'Site',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).siteName ?? 'N/A'}`,
            },
            {
                id: 'regionName',
                field: 'regionName',
                width: 50,
                text: 'Region',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).regionName ?? 'N/A'}`,
            },
            {
                id: 'orderLineItemNumber',
                field: 'orderLineItemNumber',
                width: 50,
                text: 'Line Item #',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) =>
                    `#${getOrderLineItemFromResourceModel(data).orderLineItemNumber}/${getOrderLineItemFromResourceModel(data).lineItemCount}`,
                sortable(lhs, rhs) {
                    return numericCompare(lhs?.data?.orderLineItemNumber, rhs?.data?.orderLineItemNumber);
                },
            },
            {
                id: 'orderer',
                field: 'orderer',
                width: 150,
                text: 'Orderer',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).orderer}`,
            },
            {
                id: 'jobNumber',
                field: 'jobNumber',
                width: 50,
                text: 'Job #',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) => (
                    <Typography>
                        <Link
                            href={`/job/${getOrderLineItemFromResourceModel(data).jobId}`}
                            variant='body2'
                            onClick={() => {
                                navigate(`/job/${getOrderLineItemFromResourceModel(data).jobId}`);
                            }}>
                            #{getOrderLineItemFromResourceModel(data).jobNumber}
                        </Link>
                    </Typography>
                ),
                sortable(lhs, rhs) {
                    return numericCompare(lhs?.data?.jobNumber, rhs?.data?.jobNumber);
                },
            },
            {
                id: 'dispatchingIntervalTimeFormatted',
                field: 'dispatchingIntervalTimeFormatted',
                width: 50,
                text: 'Interval Time',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
            },
            {
                id: 'intervalCount',
                field: 'intervalCount',
                width: 50,
                text: 'Interval Count',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) => `${getOrderLineItemFromResourceModel(data).intervalCount ?? ''}`,
            },
            {
                id: 'yardTime',
                field: 'yardTime',
                width: 50,
                text: 'Yard Time',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                renderer: (data: { record: ResourceModel }) => {
                    const yardTime = getOrderLineItemFromResourceModel(data).yardTime;
                    const formattedYardTime = yardTime ? formatTime(yardTime) : 'ASAP';
                    return `${formattedYardTime}`;
                },
                groupRenderer: ({ groupRowFor }) => {
                    return groupRowFor !== '!!novalue!!' ? formatTime(groupRowFor) : 'ASAP';
                },
            },
            {
                id: 'isScheduled',
                field: 'isScheduled',
                width: 50,
                text: 'Assigned',
                enableCellContextMenu: false,
                readOnly: true,
                htmlEncode: false,
                hidden: false,
                renderer: (data: { record: ResourceModel }) => {
                    var record = getOrderLineItemFromResourceModel(data);
                    var tooltipText: string;
                    var icon: JSX.Element;
                    switch (getSchedulingStatus(record.isScheduled ?? false, record.isOverbooked ?? false)) {
                        case 'Overbooked':
                            icon = <Warning sx={{ width: 24, height: 24, color: 'orange' }} />;
                            tooltipText = 'Overbooked';
                            break;
                        case 'Scheduled':
                            icon = <CheckCircle sx={{ width: 24, height: 24, color: 'green' }} />;
                            tooltipText = 'Assigned';
                            break;
                        case 'Unscheduled':
                            icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                            tooltipText = 'Unassigned';
                            break;
                        default:
                            icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                            tooltipText = 'Unassigned';
                            break;
                    }

                    return <Tooltip title={tooltipText}>{icon}</Tooltip>;
                },
                align: 'center' as 'center' | 'left' | 'right' | 'start' | 'end' | undefined, // Weird hack to resolve bryntum not recognizing the alignment value correctly,
                sortable(lhs, rhs) {
                    return booleanCompare(lhs?.data?.isScheduled, rhs?.data?.isScheduled);
                },
            },
        ],
    }), [getOrderLineItemFromResourceModel, navigate]);

    const haulerGridConfig: BryntumGridProps = useMemo(() => ({
        stateId: 'orderDispatch_haulerGridState',
        rowHeight: 80,
        minWidth: 300,
        cls: 'b-unplannedgrid1',
        groupFeature: {
            field: 'haulerType',
            ascending: false,
        },
        sortFeature: {
            field: 'code',
            ascending: true,
            // Without this, the initial programmatic sort ignores the Code column's custom
            // comparator and falls back to a plain string sort.
            prioritizeColumns: true,
        },
        columns: {
            data: [
                {
                    id: 'name',
                    text: 'Name',
                    flex: 1,
                    field: 'name',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                },
                {
                    id: 'assignedEquipment',
                    text: 'Assigned Equipment',
                    flex: 1,
                    field: 'assignedEquipment',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                },
                {
                    id: 'isScheduled',
                    text: 'Scheduled',
                    field: 'isScheduled',
                    width: 50,
                    enableCellContextMenu: false,
                    readOnly: true,
                    htmlEncode: false,
                    hidden: false,
                    renderer: ({ record }: { record: any }) => {
                        var tooltipText: string;
                        var icon: JSX.Element;
                        switch (getSchedulingStatus(record.isScheduled ?? false, record.isOverbooked ?? false)) {
                            case 'Overbooked':
                                icon = <Warning sx={{ width: 24, height: 24, color: 'orange' }} />;
                                tooltipText = 'Overbooked';
                                break;
                            case 'Scheduled':
                                icon = <CheckCircle sx={{ width: 24, height: 24, color: 'green' }} />;
                                tooltipText = 'Scheduled';
                                break;
                            case 'Unscheduled':
                                icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                                tooltipText = 'Unscheduled';
                                break;
                            default:
                                icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                                tooltipText = 'Unscheduled';
                                break;
                        }

                        return <Tooltip title={tooltipText}>{icon}</Tooltip>;
                    },
                    align: 'center' as 'center' | 'left' | 'right' | 'start' | 'end' | undefined, // Weird hack to resolve bryntum not recognizing the alignment value correctly,
                    sortable(lhs, rhs) {
                        return booleanCompare(lhs?.data?.isScheduled, rhs?.data?.isScheduled);
                    },
                },
                {
                    id: 'isCurrentlyAvailable',
                    text: 'Available',
                    field: 'isCurrentlyAvailable',
                    width: 50,
                    enableCellContextMenu: false,
                    readOnly: true,
                    htmlEncode: false,
                    hidden: false,
                    renderer: ({ record }: { record: any }) => {
                        const isAvailable = record.data?.isCurrentlyAvailable;
                        if (isAvailable === null || isAvailable === undefined) {
                            return null;
                        }
                        const tooltipText = isAvailable ? 'Currently Available' : 'Not Currently Available';
                        const icon = isAvailable ? (
                            <CheckCircle sx={{ width: 24, height: 24, color: 'green' }} />
                        ) : (
                            <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />
                        );
                        return <Tooltip title={tooltipText}>{icon}</Tooltip>;
                    },
                    align: 'center' as 'center' | 'left' | 'right' | 'start' | 'end' | undefined,
                    sortable(lhs, rhs) {
                        const lhsValue = lhs?.data?.isCurrentlyAvailable;
                        const rhsValue = rhs?.data?.isCurrentlyAvailable;
                        const lhsMissing = lhsValue === null || lhsValue === undefined;
                        const rhsMissing = rhsValue === null || rhsValue === undefined;
                        if (lhsMissing && rhsMissing) return 0;
                        if (lhsMissing) return 1;
                        if (rhsMissing) return -1;
                        return lhsValue === rhsValue ? 0 : lhsValue ? -1 : 1;
                    },
                },
                {
                    id: 'code',
                    text: 'Code',
                    flex: 1,
                    field: 'code',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                    sortable: codeSortable,
                },
                {
                    id: 'haulerType',
                    text: 'Hauler Type',
                    flex: 1,
                    field: 'haulerType',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                },
                {
                    id: 'hireDate',
                    text: 'Hire Date',
                    flex: 1,
                    field: 'hireDate',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    sortable: (lhs, rhs) => dateCompare(lhs?.data?.hireDate, rhs?.data?.hireDate),
                },
            ],
        },
        stripeFeature: true,
    }), []);

    const haulerViewSchedulerDefaultColumConfig: ColumnStoreConfig = useMemo(() => ({
        data: [
            {
                id: 'name',
                text: 'Name',
                width: 50,
                field: 'name',
                locked: true,
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'assignedEquipment',
                text: 'Assigned Equipment',
                width: 50,
                field: 'assignedEquipment',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
            },
            {
                id: 'isScheduled',
                text: 'Scheduled',
                width: 50,
                field: 'isScheduled',
                enableCellContextMenu: false,
                readOnly: true,
                htmlEncode: false,
                renderer: ({ record }: { record: any }) => {
                    var tooltipText: string;
                    var icon: JSX.Element;
                    switch (getSchedulingStatus(record.isScheduled ?? false, record.isOverbooked ?? false)) {
                        case 'Overbooked':
                            icon = <Warning sx={{ width: 24, height: 24, color: 'orange' }} />;
                            tooltipText = 'Overbooked';
                            break;
                        case 'Scheduled':
                            icon = <CheckCircle sx={{ width: 24, height: 24, color: 'green' }} />;
                            tooltipText = 'Scheduled';
                            break;
                        case 'Unscheduled':
                            icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                            tooltipText = 'Unscheduled';
                            break;
                        default:
                            icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                            tooltipText = 'Unscheduled';
                            break;
                    }

                    return <Tooltip title={tooltipText}>{icon}</Tooltip>;
                },
                align: 'center' as 'center' | 'left' | 'right' | 'start' | 'end' | undefined, // Weird hack to resolve bryntum not recognizing the alignment value correctly
                hidden: false,
            },
            {
                id: 'isCurrentlyAvailable',
                text: 'Available',
                width: 50,
                field: 'isCurrentlyAvailable',
                enableCellContextMenu: false,
                readOnly: true,
                htmlEncode: false,
                hidden: false,
                renderer: ({ record }: { record: any }) => {
                    const isAvailable = record.data?.isCurrentlyAvailable;
                    if (isAvailable === null || isAvailable === undefined) {
                        return null;
                    }
                    const tooltipText = isAvailable ? 'Currently Available' : 'Not Currently Available';
                    const icon = isAvailable ? (
                        <CheckCircle sx={{ width: 24, height: 24, color: 'green' }} />
                    ) : (
                        <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />
                    );
                    return <Tooltip title={tooltipText}>{icon}</Tooltip>;
                },
                align: 'center' as 'center' | 'left' | 'right' | 'start' | 'end' | undefined,
            },
            {
                id: 'code',
                text: 'Code',
                width: 50,
                field: 'code',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: false,
                sortable: codeSortable,
            },
            {
                id: 'haulerType',
                text: 'Hauler Type',
                width: 50,
                field: 'haulerType',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
            },
            {
                text: 'Hire Date',
                width: 50,
                field: 'hireDate',
                enableCellContextMenu: false,
                readOnly: true,
                hidden: true,
                sortable: (lhs, rhs) => dateCompare(lhs?.data?.hireDate, rhs?.data?.hireDate),
            },
        ],
    }), []);

    const orderLineItemGridConfig: BryntumGridProps = useMemo(() => ({
        stateId: 'haulerManagement_orderGridState',
        rowHeight: 80,
        minWidth: 300,
        cls: 'b-unplannedgrid1',
        groupFeature: {
            field: 'customerName',
            ascending: false,
        },
        store: {
            sorters: [
                {
                    field: 'orderNumber',
                    ascending: true,
                },
                {
                    field: 'orderLineItemNumber',
                    ascending: true,
                },
            ]
        },
        columns: {
            data: [
                {
                    id: 'customerName',
                    field: 'customerName',
                    flex: 1,
                    text: 'Customer',
                    locked: true,
                    cellCls: 'dispatch-customer-name-cell',
                    enableCellContextMenu: false,
                    readOnly: true,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).customerName}`,
                    hidden: false,
                },
                {
                    id: 'customerNumber',
                    field: 'customerNumber',
                    width: 50,
                    text: 'Customer #',
                    enableCellContextMenu: false,
                    readOnly: true,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).customerNumber}`,
                    hidden: false,
                },
                {
                    id: 'onsiteTime',
                    field: 'onsiteTime',
                    width: 50,
                    text: 'Onsite Time',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                    renderer: (data: { record: Model }) => {
                        const onsiteTime = getOrderLineItemFromResourceModel(data).onsiteTime;
                        const formattedOnsiteTime = onsiteTime ? formatTime(onsiteTime) : 'ASAP';
                        return `${formattedOnsiteTime}`;
                    },
                    groupRenderer: ({ groupRowFor }) => {
                        return groupRowFor !== '!!novalue!!' ? formatTime(groupRowFor) : 'ASAP';
                    },
                },
                {
                    id: 'orderNumber',
                    field: 'orderNumber',
                    width: 50,
                    text: 'Order #',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                    renderer: (data: { record: Model }) => (
                        <Typography>
                            <Link
                                href={`/job/${getOrderLineItemFromResourceModel(data).jobId}/order/${getOrderLineItemFromResourceModel(data).orderId}`}
                                variant='body2'
                                onClick={() => {
                                    navigate(`/job/${getOrderLineItemFromResourceModel(data).jobId}/order/${getOrderLineItemFromResourceModel(data).orderId}`);
                                }}>
                                #{getOrderLineItemFromResourceModel(data).orderNumber}
                            </Link>
                        </Typography>
                    ),
                    sortable: orderNumberSortable,
                },
                {
                    id: 'poNumber',
                    field: 'poNumber',
                    width: 50,
                    text: 'PO #',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).poNumber ?? ''}`,
                },
                {
                    id: 'projectName',
                    field: 'projectName',
                    width: 50,
                    text: 'Project',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                },
                {
                    id: 'jobName',
                    field: 'jobName',
                    width: 50,
                    text: 'Job',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                },
                {
                    id: 'description',
                    field: 'name',
                    width: 50,
                    text: 'Description',
                    enableCellContextMenu: false,
                    hidden: false,
                    readOnly: false,
                },
                {
                    id: 'equipmentType',
                    field: 'equipmentTypeType',
                    width: 50,
                    text: 'Equipment Type',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                },
                {
                    id: 'requirements',
                    field: 'requirements',
                    width: 50,
                    text: 'Requirements',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: false,
                },
                {
                    id: 'isScheduled',
                    field: 'isScheduled',
                    width: 50,
                    text: 'Assigned',
                    enableCellContextMenu: false,
                    readOnly: true,
                    htmlEncode: false,
                    hidden: false,
                    renderer: (data: { record: ResourceModel }) => {
                        var record = getOrderLineItemFromResourceModel(data);
                        var tooltipText: string;
                        var icon: JSX.Element;
                        switch (getSchedulingStatus(record.isScheduled ?? false, record.isOverbooked ?? false)) {
                            case 'Overbooked':
                                icon = <Warning sx={{ width: 24, height: 24, color: 'orange' }} />;
                                tooltipText = 'Overbooked';
                                break;
                            case 'Scheduled':
                                icon = <CheckCircle sx={{ width: 24, height: 24, color: 'green' }} />;
                                tooltipText = 'Assigned';
                                break;
                            case 'Unscheduled':
                                icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                                tooltipText = 'Unassigned';
                                break;
                            default:
                                icon = <Cancel sx={{ width: 24, height: 24, color: '#A3271F' }} />;
                                tooltipText = 'Unassigned';
                                break;
                        }

                        return <Tooltip title={tooltipText}>{icon}</Tooltip>;
                    },
                    align: 'center' as 'center' | 'left' | 'right' | 'start' | 'end' | undefined, // Weird hack to resolve bryntum not recognizing the alignment value correctly,
                    sortable(lhs, rhs) {
                        return booleanCompare(lhs?.data?.isScheduled, rhs?.data?.isScheduled);
                    },
                },
                {
                    id: 'siteName',
                    field: 'siteName',
                    width: 50,
                    text: 'Site',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).siteName ?? 'N/A'}`,
                },
                {
                    id: 'regionName',
                    field: 'regionName',
                    width: 50,
                    text: 'Region',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).regionName ?? 'N/A'}`,
                },
                {
                    id: 'orderLineItemNumber',
                    field: 'orderLineItemNumber',
                    width: 50,
                    text: 'Line Item #',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) =>
                        `#${getOrderLineItemFromResourceModel(data).orderLineItemNumber}/${getOrderLineItemFromResourceModel(data).lineItemCount}`,
                    sortable(lhs: any, rhs: any) {
                        return numericCompare(lhs?.data?.orderLineItemNumber, rhs?.data?.orderLineItemNumber);
                    },
                },
                {
                    id: 'orderer',
                    field: 'orderer',
                    width: 150,
                    text: 'Orderer',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).orderer}`,
                },
                {
                    id: 'jobNumber',
                    field: 'jobNumber',
                    width: 50,
                    text: 'Job #',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) => (
                        <Typography>
                            <Link
                                href={`/job/${getOrderLineItemFromResourceModel(data).jobId}`}
                                variant='body2'
                                onClick={() => {
                                    navigate(`/job/${getOrderLineItemFromResourceModel(data).jobId}`);
                                }}>
                                #{getOrderLineItemFromResourceModel(data).jobNumber}
                            </Link>
                        </Typography>
                    ),
                    sortable(lhs: any, rhs: any) {
                        return numericCompare(lhs?.data?.jobNumber, rhs?.data?.jobNumber);
                    },
                },
                {
                    id: 'dispatchingIntervalTimeFormatted',
                    field: 'dispatchingIntervalTimeFormatted',
                    width: 50,
                    text: 'Interval Time',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                },

                {
                    id: 'intervalCount',
                    field: 'intervalCount',
                    width: 50,
                    text: 'Interval Count',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) => `${getOrderLineItemFromResourceModel(data).intervalCount ?? ''}`,
                },
                {
                    id: 'yardTime',
                    field: 'yardTime',
                    width: 50,
                    text: 'Yard Time',
                    enableCellContextMenu: false,
                    readOnly: true,
                    hidden: true,
                    renderer: (data: { record: Model }) => {
                        const yardTime = getOrderLineItemFromResourceModel(data).yardTime;
                        const formattedYardTime = yardTime ? formatTime(yardTime) : 'ASAP';
                        return `${formattedYardTime}`;
                    },
                    groupRenderer: ({ groupRowFor }) => {
                        return groupRowFor !== '!!novalue!!' ? formatTime(groupRowFor) : 'ASAP';
                    },
                },
            ],
        },
        stripeFeature: true,
    }), [getOrderLineItemFromResourceModel, navigate]);

    return {
        orderViewSchedulerDefaultColumnConfigs,
        haulerGridConfig,
        haulerViewSchedulerDefaultColumConfig,
        orderLineItemGridConfig
    }
}